import React, { Component, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import noop from 'lodash/noop';
// eslint-disable-next-line @atlaskit/design-system/no-unsupported-drag-and-drop-libraries
import {
	DragDropContext,
	Droppable,
	type DropResult,
	type ResponderProvided,
} from 'react-beautiful-dnd';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import { token } from '@atlaskit/tokens';
import ErrorBoundary from '@atlassian/jira-error-boundary/src/ErrorBoundary.tsx';
import { AnalyticsEventToProps } from '@atlassian/jira-product-analytics-bridge';
import ItemLineCard, { type Item, type ItemLineCardProps } from './item-line-card-view.tsx';

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const Container = styled.ul<{ placeholder?: ReactNode }>({
	listStyleType: 'none',
	boxShadow: token('elevation.shadow.raised'),

	backgroundColor: token('elevation.surface.raised'),
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('radius.small', '3px'),
	paddingTop: token('space.025'),
	paddingRight: 0,
	paddingBottom: token('space.025'),
	paddingLeft: 0,
});

type ContainerProps = React.ClassAttributes<HTMLUListElement> &
	React.HTMLAttributes<HTMLUListElement> & {
		placeholder?: ReactNode;
		// this is added for react-beautiful-dnd
		isDraggingOver?: boolean;
	};

export const DragDropContextWithAnalytics = AnalyticsEventToProps('issueRank', {
	onDragEnd: 'updated',
})(DragDropContext);

type Props<T extends Item> = Omit<ItemLineCardProps<T>, 'item' | 'isVariableHeight' | 'index'> & {
	items: ReadonlyArray<T>;
	isVariableHeightCards: ItemLineCardProps<T>['isVariableHeight'];
	groupId: string;
	onOrderChange: (
		items: ReadonlyArray<T>,
		target: T,
		sourceIdx: number,
		destinationIdx: number,
		analyticsEvent: UIAnalyticsEvent,
	) => void;
};

/**
 * Manages a collection of draggable cards representing items, enabling users to reorder them through drag-and-drop interactions. It supports customization of card behavior, such as enabling reordering, interactivity, and adjusting card heights. This class also handles user actions like clicking and reordering, facilitating dynamic, user-driven layouts.
 */
// eslint-disable-next-line jira/react/no-class-components
export default class ItemLineCardGroup<T extends Item> extends Component<Props<T>> {
	static defaultProps = {
		isReorderEnabled: false,
		isInteractive: true,
		isVariableHeightCards: false,
		onOrderChange: noop,
		onClick: noop,
	};

	onDragEnd = (
		result: DropResult,
		provided: ResponderProvided,
		analyticsEvent: UIAnalyticsEvent,
	) => {
		const { source } = result;
		const { destination } = result;
		if (!destination || source.droppableId !== destination.droppableId) {
			return;
		}
		const items = [...this.props.items];
		const target = items.find((item) => item.id === result.draggableId);
		if (!target) {
			return;
		}

		// Move the dropped item into the correct spot
		items.splice(source.index, 1);
		items.splice(destination.index, 0, target);
		this.props.onOrderChange(items, target, source.index, destination.index, analyticsEvent);
	};

	renderCards(cardsProps: ContainerProps = {}) {
		const { isReorderEnabled, isInteractive, isVariableHeightCards, onClick } = this.props;
		const { placeholder, ...rest } = cardsProps;
		return (
			<Container {...rest}>
				{this.props.items.map((item, index) => {
					const itemLineCardProps: ItemLineCardProps<T> & { key?: string } = {
						key: item.id,
						item,
						index,
						isReorderEnabled,
						isInteractive,
						isVariableHeight: isVariableHeightCards,
						onClick,
						children: this.props.children,
					};
					const itemLineCard = (
						<ErrorBoundary key={item.id}>
							<ItemLineCard {...itemLineCardProps} />
						</ErrorBoundary>
					);
					return itemLineCard;
				})}
				{placeholder}
			</Container>
		);
	}

	renderDraggableCards() {
		const dragDropContext = (
			<DragDropContextWithAnalytics onDragEnd={this.onDragEnd}>
				<Droppable droppableId={this.props.groupId}>
					{(provided, snapshot) => (
						<>
							{this.renderCards({
								isDraggingOver: snapshot.isDraggingOver,
								placeholder: provided.placeholder,
								ref: provided.innerRef,
							})}
						</>
					)}
				</Droppable>
			</DragDropContextWithAnalytics>
		);
		return dragDropContext;
	}

	render() {
		if (!this.props.isReorderEnabled) {
			return this.renderCards();
		}
		return this.renderDraggableCards();
	}
}
